<template>
  <div class="field autocomplete">
    <input
      name="sensis-input"
      placeholder="Full address"
      type="text"
      v-model="inputText"
      @input="checkAddress"
      @keydown.enter.prevent="keyEnter"
      @keydown.up.capture="keyUp"
      @keydown.down.capture="keyDown"
      @keydown.tab.prevent="keyEnter"
      @keydown.escape.prevent="keyEscape"
      @blur="displaySuggestions = false"
      autocomplete="nope"
      @click="clickInside()"
      maxlength="400"
    />

    <!-- Loading when fetching address' details -->
    <div class="input-loading" v-if="fetchingDetails">
      <img alt="Loading while fetching address' detail" src="@/assets/img/loading-tail-spin.svg" />
    </div>

    <div class="suggestions" v-if="displaySuggestions">
      <!-- LOADING -->
      <div class="suggestion-loading" v-if="isLoading">
        <img alt="Loading while fetching suggestions" src="@/assets/img/loading-three-dots.svg" />
      </div>

      <!-- LIST OF SUGGESTIONS -->
      <div id="sensis-suggestion-list" class="suggestion-list" v-if="!isLoading && suggestions.length > 0">
        <!-- List -->
        <div
          :id="'sensis-suggestion-item-' + p.index"
          class="suggestion-item"
          :class="{ 'item-active': cursor === p.index }"
          @mousedown="selectAddress(p)"
          @mouseover="cursor = p.index"
          v-for="p in suggestions"
          :key="p.index"
        >
          {{ p.formatted_address }}
        </div>
      </div>

      <div class="suggestion-list" v-if="!isLoading && suggestions.length == 0">
        <!-- Empty -->
        <div class="suggestion-item empty" @click="keyEscape">No Suggestions</div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api';

export default {
  name: 'AutocompleteAddress',

  data() {
    return {
      inputText: '',
      suggestions: [],
      cursor: 1,
      displaySuggestions: false,
      isLoading: false,
      timeout: null,
      delay: 600, // ms
      minInputLength: 4,
      lastTimestamp: Date.now(),
      fetchingDetails: false,
    };
  },
  methods: {
    clickInside() {
      if (this.inputText && this.inputText.length >= this.minInputLength) {
        this.displaySuggestions = true;
      }
    },
    checkAddress() {
      if (this.timeout) {
        clearTimeout(this.timeout);
        this.timeout = null;
      }

      // In any case, if the input changed, the suggestions will change
      this.suggestions = [];

      if (this.inputText && this.inputText.length >= this.minInputLength) {
        this.isLoading = true;
        let timestamp = Date.now(); // timestamp for this api call
        this.lastTimestamp = timestamp; // 1 , 2 , 3
        this.timeout = setTimeout(() => {
          this.displaySuggestions = true;
          const input = this.inputText;

          // Fetch Suggestions
          api.sensisApi
            .parseAddress(input)
            .then((response) => {
              if (response && timestamp >= this.lastTimestamp) {
                if (response.status === 'OK') {
                  this.suggestions = response.results;
                }
                this.isLoading = false;
              }
            })
            .catch((err) => {
              this.isLoading = false;
              console.error(err);
            });
        }, this.delay);
      } else {
        this.displaySuggestions = false;
        this.isLoading = false;
      }
    },
    selectAddress(item) {
      this.inputText = item.formatted_address;
      this.isLoading = false;
      this.suggestions = [];
      this.fetchingDetails = true;

      api.sensisApi
        .getAddressDetails(item.search_result_id)
        .then((response) => {
          if (response.result) {
            // Format with First letter in uppercase to match our format
            const country = response.result.country.charAt(0) + response.result.country.slice(1).toLowerCase();

            const selectedAddress = {
              unitNumber: response.result.subpremise,
              streetNumber: response.result.street_number,
              streetName: `${response.result.street_name} ${response.result.street_type}`,
              postalCode: response.result.postcode,
              state: response.result.state,
              suburb: response.result.suburb,
              country: country,
            };

            if (this.inputText.toLowerCase().startsWith('po box ')) {
              selectedAddress.streetNumber = 'PO BOX';
              selectedAddress.streetName = response.result.street_address.toLowerCase().replace('po box ', '');
            }

            this.fetchingDetails = false;
            this.$emit('addressSelected', { address: selectedAddress, fullLine: this.inputText });
          }
        })
        .catch((err) => {
          this.suggestions = [];
          this.isLoading = false;
          this.fetchingDetails = false;
          console.error(err);
        });
    },
    keyUp() {
      if (this.displaySuggestions) {
        event.preventDefault();
        if (this.cursor > 1) {
          this.cursor -= 1;
          this.scrollToIndex(this.cursor);
        } else if (this.cursor === 1) {
          this.cursor = this.suggestions.length;
          this.scrollToIndex(this.cursor);
        }
      }
    },
    keyDown() {
      if (this.displaySuggestions) {
        event.preventDefault();
        if (this.cursor < this.suggestions.length) {
          this.cursor += 1;
          this.scrollToIndex(this.cursor);
        } else if (this.cursor === this.suggestions.length) {
          this.cursor = 1;
          this.scrollToIndex(this.cursor);
        }
      }
    },
    keyEnter() {
      if (this.displaySuggestions && this.suggestions[this.cursor - 1]) {
        this.selectAddress(this.suggestions[this.cursor - 1]);
        this.displaySuggestions = false;
      }
    },
    keyEscape() {
      this.displaySuggestions = false;
    },
    scrollToIndex(index) {
      const listElement = document.getElementById(`sensis-suggestion-list`);
      const itemElement = document.querySelector(`#sensis-suggestion-list .suggestion-item`);
      const itemHeight = itemElement.offsetHeight;
      const scrollTop = listElement.scrollTop;
      const viewport = scrollTop + listElement.offsetHeight;
      const scrollOffset = itemHeight * index - itemHeight;

      if (scrollOffset < scrollTop || scrollOffset + itemHeight > viewport) {
        listElement.scrollTo({
          top: scrollOffset,
          behavior: 'smooth',
        });
      }
    },
    reset() {
      this.inputText = '';
    },
  },
};
</script>

<style>
.autocomplete {
  position: relative;
}

.autocomplete input {
  margin-bottom: 0px !important;
}

.autocomplete .suggestions {
  z-index: 10;
  position: absolute;
  width: 100%;
  box-shadow: 0 8px 8px 0 rgba(51, 51, 79, 0.3);
  background-color: #fff;
}

.autocomplete .suggestions .suggestion-loading {
  display: flex;
  justify-content: center;
  padding: 18px 0;
}

.autocomplete .suggestions .suggestion-loading img {
  height: 10px;
}

.autocomplete .suggestions .suggestion-list {
  /* 31 * 7 */
  max-height: 231px;
  overflow-y: auto;
  overflow-x: hidden;
}

.autocomplete .suggestions .suggestion-list .suggestion-item {
  width: 100%;
  height: 31px;
  padding: 6px 10px;
  white-space: nowrap;
  text-align: left;
  font-size: 14px;
  background-color: #fff;
  cursor: pointer;
}

.autocomplete .suggestions .suggestion-list .suggestion-item:focus {
  background-color: #dedede;
}

.autocomplete .suggestions .suggestion-list .suggestion-item.empty {
  display: flex;
  justify-content: center;
  margin: 0;
  color: #828282;
}

.autocomplete .suggestions .suggestion-list .suggestion-item.item-active {
  background-color: #dedede;
}

.autocomplete .suggestions .suggestion-list::-webkit-scrollbar {
  width: 5px;
  height: 5px;
}

.autocomplete .suggestions .suggestion-list::-webkit-scrollbar-track {
  background: #c8c8c8;
}

.autocomplete .suggestions .suggestion-list::-webkit-scrollbar-thumb {
  background: #f3f3f3;
}
</style>
